import { light } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useMemo } from 'react';
import { Container } from 'react-bootstrap';
import Blockquote from '../../../../components/Blockquote';
import NumberInput from '../../../../components/NumberInput/NumberInput';
import Radio, { RadioGroup } from '../../../../components/Radio';
import {
  addOrRemoveYearsToGivenDate,
  DateFormats,
  formatDate,
  getEffectiveDate,
} from '../../../../utils/utils';

export type FederalTaxFormProps = {
  data: any;
  collectData: (data: any) => void;
};

const FederalTaxForm = ({ data, collectData }: FederalTaxFormProps) => {
  const { step1, step2, step3, step4 } = data;

  const handleFilingStatusChange = (e: string | number) => {
    collectData({
      ...data,
      step1: { filingStatus: e },
    });
  };

  const handleTaxablePay = (e: number | null | undefined) => {
    collectData({
      ...data,
      step2: {
        ...step2,
        taxablePay: e,
      },
    });
  };

  const handleAnnuallityPay = (e: number | null | undefined) => {
    collectData({
      ...data,
      step2: {
        ...step2,
        annuallyPay: e,
      },
    });
  };

  const handleChildrenNumber = (e: number | null | undefined) => {
    collectData({
      ...data,
      step3: {
        ...step3,
        children: e,
      },
    });
  };

  const handleDependeptsNumber = (e: number | null | undefined) => {
    collectData({
      ...data,
      step3: {
        ...step3,
        dependepts: e,
      },
    });
  };

  const handleAdditionalAmountNumber = (e: number | null | undefined) => {
    collectData({
      ...data,
      step3: {
        ...step3,
        additionalAmount: e,
      },
    });
  };

  const handleOtherIncome = (e: number | null | undefined) => {
    collectData({
      ...data,
      step4: {
        ...step4,
        otherIncome: e,
      },
    });
  };

  const handleDeductions = (e: number | null | undefined) => {
    collectData({
      ...data,
      step4: {
        ...step4,
        deductions: e,
      },
    });
  };

  const handleExtraWithholdings = (e: number | null | undefined) => {
    collectData({
      ...data,
      step4: {
        ...step4,
        extraWithholdings: e,
      },
    });
  };

  const calculateTotalTaxPay = useMemo(() => {
    const { taxablePay, annuallyPay } = step2;

    let totalSum = 0;

    if (taxablePay) {
      totalSum += taxablePay;
    }
    if (annuallyPay) {
      totalSum += annuallyPay;
    }

    return totalSum;
  }, [step2]);

  const calculateChildrenNumber = useMemo(() => {
    const { children } = step3;

    let totalSum = 0;

    if (children) {
      totalSum += children;
    }

    return totalSum * 2000;
  }, [step3]);

  const calculateDependeptsNumber = useMemo(() => {
    const { dependepts } = step3;

    let totalSum = 0;

    if (dependepts) {
      totalSum += dependepts;
    }

    return totalSum * 500;
  }, [step3]);

  const calculateTotalAmountStep3 = useMemo(() => {
    const { additionalAmount } = step3;

    let totalSum = 0;

    if (additionalAmount) {
      totalSum += additionalAmount;
    }

    if (calculateChildrenNumber) {
      totalSum += calculateChildrenNumber;
    }

    if (calculateDependeptsNumber) {
      totalSum += calculateDependeptsNumber;
    }

    return totalSum;
  }, [calculateChildrenNumber, calculateDependeptsNumber, step3]);

  return (
    <Container className="p-0">
      <h6 className="lh-xs">{`W-4P ${formatDate(
        new Date(),
        DateFormats.ONLY_YEAR
      )}`}</h6>
      <section className="py-7 px-10 border rounded-2">
        <span className="text-caption lh-xs text-light-grey-normal fw-normal">
          STEP 1
        </span>
        <h6>Enter Personal Information - Filing Status</h6>
        <div className="mt-7 lh-xs">
          <RadioGroup
            name={'personal-information'}
            value={step1.filingStatus}
            onChange={handleFilingStatusChange}
          >
            <Radio
              value={'Single or Married'}
              id={'4'}
              label={'Single or Married filing separately'}
              checked={step1.filingStatus === 'Single or Married'}
            />
            <Radio
              value={'Married filing Jointly or Qualifying widow(er)'}
              id={'5'}
              label={'Married filing Jointly or Qualifying widow(er)'}
              className="mt-3"
            />
            <Radio
              value={'Head of household'}
              id={'6'}
              label={
                'Head of household (Check only if youre unmarried and pay more than half the costs of keeping up a home for yourself and a qualifying individual.)'
              }
              className="mt-3"
            />
          </RadioGroup>
        </div>
      </section>
      <section className="mt-7">
        <Blockquote variant="white">
          <span>
            Complete Steps 2-4 ONLY if they apply to you; othewise, skip to Step
            5. See page 2 for more information on each step and how to elect to
            have no federal income tax withheld (if permitted).
          </span>
        </Blockquote>
      </section>
      <section className="py-7 px-10 border rounded-2 mt-7">
        <span className="text-caption lh-xs text-light-grey-normal fw-normal">
          STEP 2
        </span>
        <h6 className="mb-0 lh-xs">
          Income From a Job or Multiple Pensions/Annuities (Including a Spouse's
          Job or Pension/Annuity)
        </h6>
        <span className="text-caption lh-xs text-light-grey-normal fw-light">
          Complete this step if you (1) receive income from a job or more than
          one pension/annuity, or (2) are married filing jointly and your spouse
          receives income from a job or a pension/annuity.
        </span>
        <div className="d-flex flex-column mt-7">
          <span className="text-caption lh-xs text-dark-gray fw-light">
            Do one of the following
          </span>
          <span className="text-caption lh-xs text-dark-gray fw-light mt-7 ps-10">
            a. Use the estimator at www.irs.gov/W4App for this step and Steps
            3-4. If you or your spouse have self-employment income, use this
            option.
          </span>
          <span className="text-caption lh-xs text-dark-gray fw-light ps-10 pt-3">
            b. Complete the items below.
          </span>
        </div>
        <div className="ms-10 ms-lg-13">
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-10">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              If you (or your spouse) have a job(s), enter the total taxable
              annual pay from all job(s) and any other income entered on Form
              W-4, Step 4(a), less the deductions entered on Form W-4, Step
              4(b). If you (or your spouse) do not have a job(s), enter "-0-"
            </span>
            <div className="number-input-size">
              <NumberInput
                icon={light('dollar')}
                placeholder={'0'}
                defaultValue={0}
                className="mt-10 mt-lg-0"
                onChange={handleTaxablePay}
                value={step2.taxablePay || 0}
                minValue={'0'}
                maxValue={'17'}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              If you (or your spouse) have another pension/annuity that pays
              less annually than this pension/annuity, enter the total annual
              taxable payments from those other sources. If this is the only
              pension/annuity or it pays the least taxable amount annually,
              enter "-0-"
            </span>
            <div className="number-input-size">
              <NumberInput
                icon={light('dollar')}
                placeholder={'0'}
                defaultValue={0}
                className="mt-10 mt-lg-0"
                onChange={handleAnnuallityPay}
                value={step2.annuallyPay || 0}
                minValue={'0'}
                maxValue={'17'}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              Add the amounts from items (i) and (ii) and enter the total here
            </span>
            <div className="number-input-size">
              <NumberInput
                icon={light('dollar')}
                placeholder={'0'}
                className="mt-10 mt-lg-0"
                value={calculateTotalTaxPay}
                disabled
                minValue={'0'}
                maxValue={'17'}
              />
            </div>
          </div>
        </div>
        <div className="mt-7">
          <Blockquote variant="white">
            <span className="text-caption fw-light text-dark-gray">
              <p>
                {`TIP: To be accurate, submit a ${formatDate(
                  new Date(),
                  DateFormats.ONLY_YEAR
                )} Form W-4P for all other
                pensions/annuities. Submit a new Form W-4 for your job(s) if you
                have not updated your withholding since ${formatDate(
                  addOrRemoveYearsToGivenDate(new Date(), -1),
                  DateFormats.ONLY_YEAR
                )}.`}
              </p>
              <p>
                If you (or your spouse) have a job, do not complete Steps 3–4(b)
                on this form.
              </p>
              <p className="mb-0">
                If you (or your spouse) do not have a job, complete Steps 3–4(b)
                on Form W-4P for only the pension/annuity that pays the most
                annually. Leave those steps blank for the other
                pensions/annuities.
              </p>
            </span>
          </Blockquote>
        </div>
      </section>
      <section className="py-7 px-10 border rounded-2 mt-7">
        <span className="text-caption lh-xs text-light-grey-normal fw-normal">
          STEP 3
        </span>
        <h6 className="mb-0 lh-xs">Claim Dependent and Other Credits</h6>
        <span className="text-caption lh-xs text-light-grey-normal fw-light">
          If your total income will be $200,000 or less ($400,000 or less if
          married filing jointly):
        </span>
        <div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              Multiply the number of qualifying children under age of 17 by
              $2,000
            </span>
            <div className="d-flex flex-row mt-3 mt-lg-0">
              <div className="number-input-size-sm">
                <NumberInput
                  icon={light('hashtag')}
                  placeholder={'0'}
                  className="me-7"
                  value={step3.children || 0}
                  onChange={handleChildrenNumber}
                  minValue={'0'}
                  maxValue={'17'}
                />
              </div>
              <div className="number-input-size">
                <NumberInput
                  icon={light('dollar')}
                  placeholder={'0'}
                  value={calculateChildrenNumber}
                  disabled
                  minValue={'0'}
                  maxValue={'17'}
                />
              </div>
            </div>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-10">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              Multiply the number of other dependents by $500
            </span>
            <div className="d-flex flex-row mt-3 mt-lg-0">
              <div className="number-input-size-sm">
                <NumberInput
                  icon={light('hashtag')}
                  placeholder={'0'}
                  value={step3.dependepts || 0}
                  className="me-7"
                  onChange={handleDependeptsNumber}
                  minValue={'0'}
                  maxValue={'17'}
                />
              </div>
              <div className="number-input-size">
                <NumberInput
                  icon={light('dollar')}
                  placeholder={'0'}
                  value={calculateDependeptsNumber}
                  disabled
                  minValue={'0'}
                  maxValue={'17'}
                />
              </div>
            </div>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              Add the amounts for qualifying children, other dependents, and
              other credits and enter the total here
            </span>
            <div className="mt-3 mt-lg-0 number-input-size">
              <NumberInput
                icon={light('dollar')}
                placeholder={'0'}
                value={step3.additionalAmount || 0}
                onChange={handleAdditionalAmountNumber}
                minValue={'0'}
                maxValue={'17'}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              Multiply the number of qualifying children under age of 17 by
              $2,000
            </span>
            <div className="mt-3 mt-lg-0 number-input-size">
              <NumberInput
                icon={light('dollar')}
                placeholder={'0'}
                value={calculateTotalAmountStep3}
                disabled
                minValue={'0'}
                maxValue={'17'}
              />
            </div>
          </div>
        </div>
      </section>
      <section className="py-7 px-10 border rounded-2 mt-7">
        <span className="text-caption lh-xs text-light-grey-normal fw-normal">
          STEP 4 (OPTIONAL)
        </span>
        <h6 className="mb-0 lh-xs">Other Adjustments</h6>
        <div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              Other income (not from jobs or pension/annuity payments). If you
              want tax withheld on other income you expect this year that won't
              have withholding, enter the amount of other income here. This may
              include interest, taxable social security, and dividends
            </span>
            <div className="mt-3 mt-lg-0 number-input-size">
              <NumberInput
                icon={light('dollar')}
                placeholder={'0'}
                value={step4.otherIncome || 0}
                onChange={handleOtherIncome}
                minValue={'0'}
                maxValue={'17'}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              Deductions If you expect to claim deductions other than the basic
              standard deduction and want to reduce your withholding, use the
              Deductions Worksheet on page 3 and enter the result here
            </span>
            <div className="mt-3 mt-lg-0 number-input-size">
              <NumberInput
                icon={light('dollar')}
                placeholder={'0'}
                value={step4.deductions || 0}
                onChange={handleDeductions}
                minValue={'0'}
                maxValue={'17'}
              />
            </div>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-lg-between mt-7">
            <span className="text-caption lh-xs text-dark-gray fw-light pe-10">
              Extra Withholding Enter any additional tax you want withheld from
              each payment
            </span>
            <div className="mt-3 mt-lg-0 number-input-size">
              <NumberInput
                icon={light('dollar')}
                placeholder={'0'}
                value={step4.extraWithholdings || 0}
                onChange={handleExtraWithholdings}
                minValue={'0'}
                maxValue={'17'}
              />
            </div>
          </div>
        </div>
      </section>
      <section className="pt-7 pb-11">
        <span className="text-caption fw-normal text-dark-gray">
          {`Effective Date: ${getEffectiveDate(new Date().getDate())}`}
        </span>
      </section>
    </Container>
  );
};

export default FederalTaxForm;
